import { parsePersonConnect } from './personConnect.js';
/** The longest ticket the card keeps (gyro-svc's are 57 characters: `<13 digits>.<43 base64url>`). */
export const RECONFIRM_TICKET_MAX = 200;
const MESSAGE_MAX = 400;
const PREVIEW_SUMMARY_MAX = 600;
/** A preview field's value on the card: a short one, and the whole text of a long one (notes and comments are 4,000 at most). */
const PREVIEW_FIELD_MAX = 300;
const PREVIEW_LONG_FIELD_MAX = 4_000;
/** The most fields one preview puts on the card; a receiver that sends more is cut, never trusted to size the card. */
const PREVIEW_FIELDS_MAX = 24;
const PREVIEW_LINKS_MAX = 20;
/** The card's own second step: what it says above Confirm and Cancel. */
export const CLIENT_CONFIRM_TEXT = 'Nothing has run yet. Confirm to go ahead, or cancel.';
/** gyro-svc's question when its answer carries none it can show. */
export const SERVER_CONFIRM_FALLBACK_TEXT = 'Nothing has run yet. Confirm once more to go ahead, or cancel.';
/** The ticket lapsed (gyro-svc's two minutes, or its preview's expiry), before Confirm or in gyro-svc's answer to it. */
export const TICKET_LAPSED_TEXT = 'Nothing ran: the confirmation timed out. Approve it again to go ahead.';
/** gyro-svc asked to confirm once more, but its answer carried no ticket the card can send back. */
export const CONFIRM_AGAIN_UNUSABLE_TEXT = 'Nothing ran: Gyro asked to confirm once more, but its answer could not be used. Approve it again to go ahead.';
/** The preview the person approved is no longer the action's preview (a fresh one replaced it, or it expired). */
export const PREVIEW_CHANGED_TEXT = 'Nothing ran: the preview changed after you approved. Check it and approve again.';
export const PREVIEW_EXPIRED_TEXT = 'Nothing ran: the preview expired, so Gyro asked for a fresh one. Check it and approve again.';
/**
 * Whether Approve sends the first confirm itself. Only for a write gyro-svc said it confirms twice: gyro-svc answers that
 * first confirm `confirm_again` and changes nothing (the flag and the answer come from the same rule, sessions.ts
 * secondConfirmationRequired). Any other write sends nothing on Approve, so nothing can run on the first press.
 */
export function approveSendsFirstConfirm(action) {
    return action.requiresSecondConfirmation === true;
}
/** The identity of a preview as the person saw it: its exact phrase, expiry and release (never its binding token). */
export function previewKey(preview) {
    if (!preview?.confirmationPhrase)
        return null;
    return JSON.stringify([preview.confirmationPhrase, preview.expiresAt ?? null, preview.implementationRevision ?? null]);
}
/** True when POST /v1/action answered gyro-svc's second confirmation (nothing ran). */
export function isConfirmAgain(response) {
    return Boolean(response) && typeof response === 'object' && response.status === 'confirm_again';
}
/**
 * gyro-svc's `confirm_again` answer as the card's second step, or null when it is not one for exactly this action with a
 * live, well-formed ticket (the card then goes back to Approve: nothing ran).
 */
export function serverConfirmStep(response, actionId, preview, nowMs = Date.now()) {
    if (!isConfirmAgain(response))
        return null;
    const answer = response;
    if (answer.actionId !== undefined && answer.actionId !== actionId)
        return null;
    const ticket = answer.reconfirm;
    if (typeof ticket !== 'string' || ticket.length === 0 || ticket.length > RECONFIRM_TICKET_MAX || !/^[\x21-\x7e]+$/.test(ticket))
        return null;
    const expiresAt = answer.reconfirmExpiresAt;
    if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt))
        return null;
    const expiresAtMs = expiresAt * 1000;
    if (expiresAtMs <= nowMs)
        return null;
    const message = typeof answer.message === 'string' && answer.message.trim() && answer.message.length <= MESSAGE_MAX
        ? answer.message.trim()
        : SERVER_CONFIRM_FALLBACK_TEXT;
    return { kind: 'server', ticket, expiresAtMs, message, preview };
}
/** The step the card shows now: a lapsed ticket is the first step again, with the line saying why. */
export function currentApprovalStep(step, nowMs) {
    if (step?.kind === 'server' && step.expiresAtMs <= nowMs)
        return { kind: 'approve', note: TICKET_LAPSED_TEXT };
    return step;
}
/** True when the card is at its second step (Confirm / Cancel). */
export function atConfirmStep(step) {
    return step?.kind === 'confirm' || step?.kind === 'server';
}
/**
 * The receiver's own plain summary of its preview (`preview.summary`, as Uplink and the other governed receivers return
 * it), bounded, or undefined when it gave none. Shown on the second step beside the action's summary.
 */
export function previewSummary(preview) {
    if (!preview || typeof preview !== 'object' || Array.isArray(preview))
        return undefined;
    const summary = preview.summary;
    if (typeof summary !== 'string')
        return undefined;
    const trimmed = summary.trim();
    if (!trimmed)
        return undefined;
    return trimmed.length > PREVIEW_SUMMARY_MAX ? `${trimmed.slice(0, PREVIEW_SUMMARY_MAX)}...` : trimmed;
}
/** The app key of the Bridge's person surface (gyro-svc bridgeMe.ts BRIDGE_ME_SURFACE_KEY): the person's own vendor accounts. */
export const PERSON_SURFACE_APP = 'bridge-me';
const PERSON_VENDOR_PREFIXES = [['asana_', 'Asana'], ['dropbox_', 'Dropbox'], ['onedrive_', 'Microsoft 365'], ['github_', 'GitHub'], ['bluebeam_', 'Bluebeam Studio']];
/**
 * The system a person-surface action runs in, for the card: the vendor the tool reaches, by its prefix (gyro-svc
 * bridgeMe.ts personToolConnectorLabel does the same for the activity row), so a change in the person's own Asana says
 * "in Asana" and "Asana says", never a raw app key. A tool of no known vendor is the Bridge's own.
 */
export function personSurfaceVendor(tool) {
    const hit = typeof tool === 'string' ? PERSON_VENDOR_PREFIXES.find(([prefix]) => tool.startsWith(prefix)) : undefined;
    return hit ? hit[1] : 'The Bridge';
}
function plainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
/** `followerCount` reads "follower count"; `My Tasks` and `what` stay as they are. */
function words(key) {
    const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : key;
}
function cut(text, max) {
    const trimmed = text.trim();
    return trimmed.length > max ? `${trimmed.slice(0, max)}...` : trimmed;
}
/**
 * A preview value as one short line: a yes/no, a number, a date or a name as it is; a list joined; an object as its own
 * fields ("completed: no"), one level deep, so `from: {completed: false}` reads as what it is, never as JSON. A null
 * reads as `nullWord`: "none" for a value that is absent (a due date not set), and, for the audience's counts, "not
 * checked", because there a null is a count the Bridge could not check and must never read as a measured zero
 * (the-bridge#1213 finding C1: "a null count means not checked; an absent key means the same").
 */
function shortValue(value, depth = 0, nullWord = 'none') {
    if (typeof value === 'string')
        return value.trim() || undefined;
    if (typeof value === 'number' && Number.isFinite(value))
        return String(value);
    if (typeof value === 'boolean')
        return value ? 'yes' : 'no';
    if (value === null)
        return nullWord;
    if (Array.isArray(value)) {
        const parts = value.map((entry) => shortValue(entry, depth + 1, nullWord)).filter((entry) => entry !== undefined);
        return parts.length ? parts.join(', ') : undefined;
    }
    if (plainObject(value) && depth < 2) {
        const parts = Object.entries(value).slice(0, 12).map(([key, entry]) => {
            const inner = shortValue(entry, depth + 1, nullWord);
            return inner === undefined ? undefined : `${words(key).toLowerCase()}: ${inner}`;
        }).filter((entry) => entry !== undefined);
        return parts.length ? parts.join('; ') : undefined;
    }
    return undefined;
}
/** A link the card may show: http(s) only, one line, bounded. Anything else is left out, never rendered. */
function linkValue(value) {
    if (typeof value !== 'string')
        return undefined;
    const trimmed = value.trim();
    if (trimmed.length > PREVIEW_FIELD_MAX || /[\s\u0000-\u001f]/.test(trimmed) || !/^https?:\/\//i.test(trimmed))
        return undefined;
    return trimmed;
}
/** A change as the Bridge's update preview lists it: `{field, from, to}` with a field name the card can say. */
function changeFields(entry) {
    if (!plainObject(entry) || typeof entry.field !== 'string' || !entry.field.trim())
        return [];
    const field = words(entry.field).toLowerCase();
    if (entry.field === 'notes') {
        const out = [];
        if (typeof entry.from === 'string')
            out.push({ label: 'Notes before', value: cut(entry.from, PREVIEW_LONG_FIELD_MAX) || '(empty)', long: true });
        if (typeof entry.to === 'string')
            out.push({ label: 'Notes after', value: cut(entry.to, PREVIEW_LONG_FIELD_MAX) || '(empty)', long: true });
        return out;
    }
    const from = shortValue(entry.from);
    const to = shortValue(entry.to);
    if (from === undefined && to === undefined)
        return [];
    const value = from !== undefined && to !== undefined ? `from ${cut(from, PREVIEW_FIELD_MAX)} to ${cut(to, PREVIEW_FIELD_MAX)}` : `to ${cut(to ?? from ?? '', PREVIEW_FIELD_MAX)}`;
    return [{ label: `Change to ${field}`, value }];
}
/**
 * The receiver's preview as labelled lines for the confirm step, so the person reads what will change in full and not
 * only the summary or a JSON line (the-bridge#1211 comment 266112411, sections 3 and 8.2; #923 12.1 item 9): what and
 * where, the task and who it is assigned to, the exact change (from and to, the whole comment, the notes before and
 * after), every link in the text on its own, who can see it and who may be notified, and the audience counts. Only
 * fields of the shapes the Bridge sends are shown, each bounded; `summary` is shown by the caller, and anything the card
 * does not know how to say is left out rather than rendered raw. An empty list means the preview has no such fields.
 */
export function previewFields(preview) {
    if (!plainObject(preview))
        return [];
    const out = [];
    const push = (label, value, long = false) => {
        if (value === undefined)
            return;
        const trimmed = long ? cut(value, PREVIEW_LONG_FIELD_MAX) : cut(value, PREVIEW_FIELD_MAX);
        if (trimmed)
            out.push(long ? { label, value: trimmed, long: true } : { label, value: trimmed });
    };
    if (typeof preview.what === 'string')
        push('What', preview.what);
    if (plainObject(preview.task)) {
        const name = typeof preview.task.name === 'string' ? preview.task.name.trim() : '';
        const id = typeof preview.task.id === 'string' ? preview.task.id.trim() : '';
        push('Task', name || id || undefined);
    }
    push('Where', shortValue(preview.where));
    if (typeof preview.assignee === 'string')
        push('Assigned to', preview.assignee);
    if (Object.hasOwn(preview, 'from'))
        push('From', shortValue(preview.from));
    if (Object.hasOwn(preview, 'to'))
        push('To', shortValue(preview.to));
    if (typeof preview.comment === 'string')
        push('Comment', preview.comment || '(empty)', true);
    if (typeof preview.notes === 'string')
        push('Notes', preview.notes || '(empty)', true);
    if (typeof preview.name === 'string')
        push('Name', preview.name);
    if (typeof preview.dueOn === 'string')
        push('Due on', preview.dueOn);
    if (Array.isArray(preview.changes))
        for (const entry of preview.changes.slice(0, PREVIEW_FIELDS_MAX))
            out.push(...changeFields(entry));
    if (Array.isArray(preview.links)) {
        const links = preview.links.slice(0, PREVIEW_LINKS_MAX).map(linkValue).filter((link) => link !== undefined);
        if (links.length)
            out.push({ label: links.length === 1 ? 'Link' : 'Links', value: links.join('\n') });
    }
    if (typeof preview.visibleTo === 'string')
        push('Visible to', preview.visibleTo);
    push('May notify', shortValue(preview.notifies));
    push('Audience', shortValue(preview.audience, 0, 'not checked'));
    return out.slice(0, PREVIEW_FIELDS_MAX);
}
/**
 * The connection a preview refusal offers the person to start, for the card's Connect control (PersonConnectControl):
 * exactly `{ family, capability }` with known values (parsePersonConnect), and only on a refusal, so a connection never
 * rides on a preview that succeeded.
 */
export function previewConnect(answer) {
    if (!answer || typeof answer.error !== 'string' || !answer.error)
        return undefined;
    return parsePersonConnect(answer.connect);
}
/**
 * One press for a call to a tool on one of the PC's own connections (an mcp_call to Gyro AI for Revit, Revu or a
 * configured server; owner, 2026-10-18 18:54 PT: "the double confirm/accept is annoying af"): the card's one button is
 * Confirm, and that press sends the confirm. Nothing the second step guarded is lost: nothing runs before the press, the
 * press is a real click (the native shell records it for the device's gesture ledger, so the PC runs the action inside
 * that click), and a write gyro-svc confirms twice (`requiresSecondConfirmation`) or one with a preview to approve keeps
 * its two presses. A read-only call never reaches a card at all: gyro-svc runs it inline (mcpReadOnly.ts).
 */
export function singlePressConfirm(action) {
    if (action.tool !== 'mcp_call' || action.requiresSecondConfirmation === true || action.requiresPreview === true)
        return false;
    // The PC's command provider (run_powershell on gyro-local-commands) keeps the two presses of #425: the person reads the
    // whole script on the first and runs it on the second (review of #583, item 3). The local file tools and every vendor
    // server are one press.
    const server = typeof action.input?.server === 'string' ? action.input.server.trim().toLowerCase() : '';
    return server !== 'gyro-local-commands';
}
/** How the person reverses each of the Bridge's Asana writes themselves, in Asana, since Gyro cannot. */
const ASANA_UNDO = {
    asana_complete_task: 'reopen the task, or complete it again',
    asana_add_comment: 'delete your comment',
    asana_set_due_date: 'change the date back',
    asana_update_task: 'change it back; the values before the change are on this card',
    asana_create_task: 'delete the task',
};
/**
 * The two lines the confirm step adds for a change in the person's own Asana account through the Bridge (the design's
 * section 8.2): that it is done as them, with their own account, so colleagues see their name on it; and that Gyro cannot
 * undo it, with how they can in Asana. Nothing for any other app or tool.
 */
export function personWriteNotes(app, tool) {
    if (app !== PERSON_SURFACE_APP || typeof tool !== 'string' || !tool.startsWith('asana_'))
        return [];
    const undo = ASANA_UNDO[tool] ?? 'change it back';
    return [
        'This is done as you, with your own Asana account, so colleagues see your name on it.',
        `Gyro cannot undo it. In Asana you can ${undo}.`,
    ];
}
//# sourceMappingURL=approvalCard.js.map